
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const UserAgreement: React.FC = () => {
  const [agreed, setAgreed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const navigate = useNavigate();

  const handleAccept = () => {
    if (agreed && acknowledged) {
      // Store agreement acceptance
      localStorage.setItem('userAgreementAccepted', new Date().toISOString());
      navigate('/dashboard');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <FileText className="w-8 h-8 text-blue-600" />
          <h1 className="text-4xl font-bold">User Agreement</h1>
        </div>
        <p className="text-xl text-muted-foreground">
          Please review and accept our terms to continue using EquiForge
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>EquiForge User Agreement</CardTitle>
            <CardDescription>
              By using EquiForge, you agree to the following terms and conditions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96 w-full border rounded-md p-4">
              <div className="space-y-4 text-sm">
                <section>
                  <h3 className="font-semibold text-base mb-2">1. Service Description</h3>
                  <p>
                    EquiForge provides mortgage calculation tools, real estate analytics, and related services 
                    designed to assist users in making informed real estate decisions. Our services include 
                    but are not limited to mortgage calculators, buying power analysis, property comparison 
                    tools, and market intelligence.
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">2. Accuracy and Limitations</h3>
                  <p>
                    All calculations and estimates provided by EquiForge are for informational purposes only 
                    and should not be considered as professional financial advice. Results may vary based on 
                    actual lending terms, market conditions, and individual circumstances. We strongly recommend 
                    consulting with qualified financial and real estate professionals before making any financial 
                    decisions.
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">3. Data Privacy and Security</h3>
                  <p>
                    We take your privacy seriously. Financial information entered into our calculators is 
                    processed locally in your browser when possible and is not stored on our servers unless 
                    you explicitly save calculations to your account. We use industry-standard security measures 
                    to protect your data and do not sell your personal information to third parties.
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">4. User Responsibilities</h3>
                  <p>
                    Users are responsible for providing accurate information when using our tools and for 
                    understanding that results are estimates only. You agree not to use our services for any 
                    unlawful purposes and to respect our intellectual property rights.
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">5. Service Availability</h3>
                  <p>
                    While we strive to maintain high service availability, we cannot guarantee uninterrupted 
                    access to our services. We reserve the right to modify, suspend, or discontinue any part 
                    of our services at any time with or without notice.
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">6. Limitation of Liability</h3>
                  <p>
                    EquiForge and its affiliates shall not be liable for any indirect, incidental, special, 
                    consequential, or punitive damages arising from your use of our services. Our total 
                    liability shall not exceed the amount paid by you for our services in the twelve months 
                    preceding the claim.
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">7. Updates to Agreement</h3>
                  <p>
                    We may update this agreement from time to time. When we do, we will post the updated 
                    agreement on our website and update the "Last Modified" date. Your continued use of 
                    our services after any changes constitutes acceptance of the new terms.
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">8. Contact Information</h3>
                  <p>
                    If you have questions about this agreement or our services, please contact us through 
                    our support channels or via the contact information provided on our website.
                  </p>
                </section>
              </div>
            </ScrollArea>

            <div className="mt-6 space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="agree"
                  checked={agreed}
                  onCheckedChange={(checked) => setAgreed(!!checked)}
                />
                <label htmlFor="agree" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  I have read and agree to the terms and conditions above
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="acknowledge"
                  checked={acknowledged}
                  onCheckedChange={(checked) => setAcknowledged(!!checked)}
                />
                <label htmlFor="acknowledge" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  I acknowledge that EquiForge calculations are estimates only and should not replace professional advice
                </label>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  onClick={handleAccept}
                  disabled={!agreed || !acknowledged}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accept and Continue
                </Button>
                <Button variant="outline" onClick={() => navigate('/')}>
                  Decline
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserAgreement;
